import React, {useState} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import {withElementRef} from '../../../components/Fields/withElementRef';
import styles from '../styles/Field.module.scss';
import {
    Title,
    Icon,
    PriorityIcon,
    Button,
    Dropdown,
    TriggerWrapper,
    withDropdownContext,
    Separator,
    Flex,
    Text
} from '@priority-software/priority-style-react';
import {
    updateTemplateReadonly,
    deleteTemplate,
    editTemplate,
    uploadTemplateFile,
    cloneTemplate,
    changeTitle
} from '../actions/Field';
import {startTitleEdit} from '../../Common/actions/Field';
import templateTexts from '../texts';
import {fieldMaxLength} from '../../../../constants/FieldConsts';
import {withTooltip} from '../../../../components/Common/HOC/Custom/withTooltip';
import moment from 'moment';
import {getDefaultDateTimeFormat} from '../../../../Localization/date';
import {getLang, isRTL} from '../../../../Localization';
import {TemplateField as TemplateFieldStore} from '../stores/TemplateField';
import {Label} from '../../../../components/Common/Label';
import {systemLang} from '../../../../Localization/languages';

const TitleWithTooltip = withTooltip(Title);

interface FieldProps {
    field: TemplateFieldStore;
    index?: number;
    langs: systemLang[];
    isEnglishHTML: boolean;
}

const Template = observer((props: FieldProps) => {
    const getDefaultLang = () => {
        return props.isEnglishHTML ? '3' : getLang();
    };

    const [title, setTitle] = useState(props.field.title);
    const [isHovering, setIsHovering] = useState(false);

    const onUpdateTemplateReadonly = () => {
        const {field} = props;

        updateTemplateReadonly(field);
    };

    const onChangeName = () => {
        const {field} = props;

        startTitleEdit(field);
    };

    const onDeleteTemplate = () => {
        const {field} = props;

        deleteTemplate(field);
    };

    const onEditTemplate = () => {
        const {field} = props;

        editTemplate(field);
    };

    const onDoneChangeName = () => {
        const {field} = props;
        changeTitle(field, title, () => {});
        field.setChanging(false);
    };

    const onCancelChange = () => {
        const {field} = props;
        setTitle(field.title);
        field.setChanging(false);
    };

    const onUploadTemplateFile = () => {
        const {field} = props;

        uploadTemplateFile(field);
    };

    const onCloneTemplate = () => {
        const {field} = props;

        cloneTemplate(field);
    };

    const getShortTitle = title => {
        if (title.length < 25) return title;
        return title.substring(0, 24) + '...';
    };
    const renderTitle = () => {
        const {
            field: {title, filetime, readOnly, isPredefined}
        } = props;
        const templateLang = props.field.lang || getDefaultLang();
        const shortTitle = getShortTitle(title);
        const tooltipPosition = isRTL() ? 'bottom right' : 'bottom left';
        //when creating a new template the filetime has not been save in the server. so when rendering the list before uploading a new template the filetime is "".
        const fixDate = filetime
            ? moment(filetime, 'DD/MM/YYYY HH:mm').format(getDefaultDateTimeFormat())
            : '';
        let date = readOnly ? `${fixDate} (${templateTexts.readOnly})` : fixDate;
        date = isPredefined ? templateTexts.predefined : date;

        return (
            <div className={styles.titleWrapper}>
                <TitleWithTooltip
                    position={tooltipPosition}
                    tooltip={title}
                    componentClasses={styles.titleText}
                    skin="h4"
                >
                    {shortTitle}
                </TitleWithTooltip>
                <Flex justifyContent="flex-start">
                    {!props.field.isDefaultTemplate ||
                        (isPredefined && (
                            <>
                                <Text
                                    componentClasses={styles.titleDate}
                                    skin="paragraph-2"
                                    textColor="secondary-500"
                                >
                                    {date}
                                </Text>
                                <Separator
                                    height={18}
                                    componentClasses={styles.descriptionSeperator}
                                />
                            </>
                        ))}
                    {!props.isEnglishHTML && (
                        <Text
                            componentClasses={styles.titleDate}
                            skin="paragraph-2"
                            textColor="secondary-500"
                        >
                            {props.langs.find(l => l.lang === templateLang)?.langtitle}
                        </Text>
                    )}
                    {fixDate && (
                        <>
                            <Separator height={18} componentClasses={styles.descriptionSeperator} />
                            <Text
                                componentClasses={styles.titleDate}
                                skin="paragraph-2"
                                textColor="secondary-500"
                            >
                                {fixDate}
                            </Text>
                        </>
                    )}
                </Flex>
            </div>
        );
    };

    const PIcon = ({dropdownContext}) => {
        return (
            <TriggerWrapper dropdownContext={dropdownContext}>
                <Icon
                    icon={PriorityIcon.threeDots}
                    skin="primary"
                    active={dropdownContext.open}
                    componentClasses={styles.iconDots}
                    onClick={e => {
                        dropdownContext.toggle();
                    }}
                />
            </TriggerWrapper>
        );
    };

    const ActionsIconTrigger = withDropdownContext(PIcon);

    const renderOldTemplateActions = () => (
        <>
            <Label skin="label-2" onClick={onUploadTemplateFile} componentClasses={styles.button}>
                {templateTexts.upload}
            </Label>

            <Separator height={18} componentClasses={styles.actionSeperator} />

            <Label skin="label-2" onClick={onEditTemplate} componentClasses={styles.button}>
                {templateTexts.download}
            </Label>

            <Dropdown
                className={styles.dotsDropdown}
                trigger={<ActionsIconTrigger />}
                openingSide="leftSide"
            >
                <Dropdown.Content>
                    <Dropdown.Item index={0} onClick={onCloneTemplate}>
                        <Label textColor="primary-700" skin="paragraph-2">
                            {templateTexts.duplicate}
                        </Label>
                    </Dropdown.Item>
                    <Dropdown.Item index={1} onClick={onDeleteTemplate}>
                        <Label textColor="primary-700" skin="paragraph-2">
                            {templateTexts.delete}
                        </Label>
                    </Dropdown.Item>
                    <Dropdown.Item index={2} onClick={onUpdateTemplateReadonly}>
                        <Label textColor="primary-700" skin="paragraph-2">
                            {props.field.readOnly
                                ? templateTexts.setEditable
                                : templateTexts.setReadOnly}
                        </Label>
                    </Dropdown.Item>
                </Dropdown.Content>
            </Dropdown>
        </>
    );

    const renderDefaultFieldActions = () => (
        <>
            <Label onClick={onCloneTemplate} componentClasses={styles.button} skin="label-2">
                {templateTexts.duplicate}
            </Label>

            <Separator height={18} componentClasses={styles.actionSeperator} />

            <Label onClick={onEditTemplate} componentClasses={styles.button} skin="label-2">
                {templateTexts.download}
            </Label>
        </>
    );
    const renderActions = () => {
        let actions;
        if (props.field.isDefaultTemplate) actions = renderDefaultFieldActions();
        else if (props.field.isNewTemplate || props.field.isActiveField)
            actions = renderNewTemplateActions();
        else actions = renderOldTemplateActions();

        return <div className={styles.actionsWrapper}>{actions}</div>;
    };

    const renderNewTemplateActions = () => (
        <>
            <Button onClick={onUploadTemplateFile} size="medium">
                {templateTexts.uploadTemplate}
            </Button>
        </>
    );

    const innerWrrpaer = classNames(styles.container, {
        [`${styles.fieldInputWrrper} template`]: props.field.isChanging,
        [styles.active]: props.field.isNewTemplate || props.field.isActiveField
    });

    const wrrpaerClasses = classNames(styles.wrraper, {
        [styles.noBorder]: props.index === 0
    });

    // TODO: @Eugenia please have a look at this function. It never used in the code.
    // const handleLangSelect = option => {
    //     setLang(option.value);
    //     showInfoMessage({
    //         id: messagesIds.screens.templateLangChange,
    //         title: templateTexts.payAttention,
    //         content: templateTexts.languageChangeWarning,
    //         isRemovable: true
    //     });
    // };

    return (
        <div
            className={wrrpaerClasses}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <div className={innerWrrpaer}>
                {props.field.isChanging ? (
                    <>
                        <input
                            type="text"
                            className={styles.titleInput}
                            maxLength={fieldMaxLength}
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                        />
                        <Icon
                            icon={PriorityIcon.check}
                            skin="standart"
                            hoverable
                            onClick={onDoneChangeName}
                        />
                        <Icon
                            icon={PriorityIcon.closeSmall}
                            skin="standart"
                            hoverable
                            onClick={onCancelChange}
                        />
                    </>
                ) : (
                    <>
                        <Flex justifyContent="flex-start">
                            <Icon
                                icon={PriorityIcon.editFill}
                                skin="standart"
                                hoverable
                                onClick={onChangeName}
                                invisible={!isHovering}
                            />
                            {renderTitle()}
                        </Flex>
                        {renderActions()}
                    </>
                )}
            </div>
        </div>
    );
});

const TemplateField = withElementRef(Template);
export default TemplateField;
